<script setup lang="ts">
  import type { Report, StructuredFinancialReport } from '@/types/Storyblok/General/StructuredFinancialReport'
  import { parseAssetPath } from '@/utils/parseAssetPath'
  import type { IButton } from '@/types/General/Button'

  const props = defineProps<{ blok: StructuredFinancialReport[]; button?: IButton }>()

  const { t } = useI18n()
  const config = useRuntimeConfig()

  const reportItems = computed(() => {
    return props.blok
      .filter((item) => {
        const fileUrl = getFileUrl(item.report)

        return [config.public.btAssetUrl, config.public.investorsUrl].some((url) => fileUrl?.includes(url))
      })
      .map((item) => ({
        ...item,
        name: getName(item.report),
        href: getFileUrl(item.report),
        fileType: item.fileType.length ? item.fileType : `${getFileType(item.report)} ${t('file')}`
      }))
  })
  const getFileUrl = (report: Report) => {
    const file = report.content?.button?.[0]?.file

    if (!file) {
      return ''
    }

    return parseAssetPath(file.cached_url ?? file.url)
  }
  const getFileType = (report: Report) => {
    const fileType = report.content?.button?.[0]?.file?.cached_url || ''

    return fileType.split('.').pop()?.toLocaleUpperCase()
  }
  const getName = (report: Report) => {
    return report.content?.button?.[0]?.name || ''
  }
</script>

<template>
  <div class="flex flex-col rounded-2xl bg-grey px-4 py-6 lg:!px-8 lg:!pb-10">
    <div class="flex flex-col gap-2">
      <NuxtLink
        v-for="item in reportItems"
        :key="item._uid"
        :href="item.href"
        class="flex items-start gap-6 rounded-lg bg-white px-4 py-4"
      >
        <NuxtImg
          :src="item.icon.filename"
          :alt="item.icon.alt"
          width="40"
          height="40"
          loading="lazy"
          format="webp"
          class="shrink-0"
        />
        <div class="flex grow flex-col gap-2 lg:h-full lg:flex-row lg:items-center lg:justify-between">
          <span
            class="text-subtitle-1"
            v-html="item.name"
          />
          <span
            class="text-secondary text-body-2"
            v-html="item.fileType"
          />
        </div>
      </NuxtLink>
    </div>
    <Button
      v-if="button"
      class="mt-8 lg:!mt-6"
      :blok="button"
    />
  </div>
</template>
